import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import { Drawer } from '../../../../components/drawers';
import toast from '../../../../utils/toast';
import { CompromisedEmployerItem } from '../hooks/useCompromisedEmployers';
import CompromisedEmployerFormFields from './CompromisedEmployerFormFields';

interface EditCompromisedEmployerFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  employer: CompromisedEmployerItem | null;
}

const EditCompromisedEmployerForm: React.FC<EditCompromisedEmployerFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  employer
}) => {
  const [reason, setReason] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employer) {
      setReason(employer.is_compromised_reason || '');
      setActive(employer.is_compromised);
    }
  }, [employer]);

  const handleClose = () => {
    setReason(employer?.is_compromised_reason || '');
    setActive(employer?.is_compromised ?? true);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employer) return;

    setLoading(true);
    try {
      await axios.post(`${environment.baseUrl}/employer/update-compromised/`, {
        employer: employer.id,
        is_compromised: active,
        is_compromised_reason: reason
      });
      toast.success('Employer updated successfully');
      onSuccess();
      handleClose();
    } catch (err: any) {
      if (err.response?.status === 400) {
        toast.error(err.response?.data?.message || 'Invalid data provided');
      } else {
        toast.error(err.response?.data?.message || 'Failed to update employer');
      }
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = reason !== (employer?.is_compromised_reason || '') || active !== employer?.is_compromised;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header title="Edit Compromised Employer" onClose={handleClose} />

      <Drawer.Content>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="employer-name" className="form-label">{/* skipcq: JS-0752 */}Employer</label>
            <input
              id="employer-name"
              type="text"
              className="form-control"
              value={employer?.name || ''}
              disabled
              readOnly
            />
          </div>
          <CompromisedEmployerFormFields
            reason={reason}
            onReasonChange={setReason}
            active={active}
            onActiveChange={setActive}
          />
        </form>
      </Drawer.Content>

      <Drawer.Footer fullWidth>
        <Drawer.PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={!hasChanges || loading}
        >
          {loading ? 'Updating...' : 'Update'}
        </Drawer.PrimaryButton>
      </Drawer.Footer>
    </Drawer>
  );
};

export default EditCompromisedEmployerForm;
